import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import ImageUpload from "../../components/image-upload/ImageUpload";
import { Avatar } from "@mui/material";
import { IMAGE_TYPES } from "../../constants";
import useSnackbar from "../../hooks/useSnackbar";
import { matrixClient } from "../../services/matrixClient";

/**
 * EditChatMatrix — Matrix version of EditChat
 *
 * Uses matrixClient service (direct Synapse or backend proxy).
 */

export default function EditChatMatrix() {
	const { chatId, clubId } = useParams<{ chatId: string; clubId: string }>();
	const [loading, setLoading] = useState(false);
	const [image, setImage] = useState<string | null>(null);
	const [chatName, setChatName] = useState<string>("");
	const [chatDescription, setChatDescription] = useState<string>("");
	const navigate = useNavigate();
	const openSnackbar = useSnackbar();

	const getChat = async () => {
		if (!chatId) {
			openSnackbar("Invalid chat ID");
			return;
		}

		try {
			setLoading(true);
			const roomInfo = await matrixClient.getRoomInfo(chatId);
			setChatName(roomInfo.name || "");
			setChatDescription(roomInfo.description || "");
			// Convert mxc:// URI to HTTP URL for display
			const img = roomInfo.image;
			setImage(img ? matrixClient.getMediaUrl(img) : null);
		} catch (error) {
			console.log(error);
			openSnackbar("Error loading chat");
		}
		setLoading(false);
	};

	useEffect(() => {
		getChat();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatId]);

	const saveChat = async () => {
		if (!chatId) {
			openSnackbar("Invalid chat ID");
			return;
		}

		try {
			setLoading(true);

			await matrixClient.updateRoomName(chatId, chatName);

			if (chatDescription) {
				await matrixClient.updateRoomDescription(chatId, chatDescription);
			}

			if (image) {
				await matrixClient.updateRoomAvatar(chatId, image);
			}

			openSnackbar("Chat updated successfully");
			navigate(`/club/${clubId}/chat/${chatId}`);
		} catch (error) {
			console.log(error);
			openSnackbar("Error updating chat");
		}
		setLoading(false);
	};

	const imageUploadHandle = (imageLink: string) => {
		setImage(imageLink);
	};

	const submitHandle = () => {
		saveChat();
	};

	return (
		<Paper
			sx={{
				p: 2,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography component="h2" variant="h6" color="primary" gutterBottom>
				Edit Chat (Matrix)
			</Typography>
			{loading && (
				<Box sx={{ width: "100%", my: 0.5 }}>
					<LinearProgress />
				</Box>
			)}
			<ImageUpload onUpload={imageUploadHandle} type={IMAGE_TYPES.CHAT_PHOTO} />
			{image && (
				<Avatar
					alt="Chat Avatar"
					src={image}
					sx={{ width: 100, height: 100, marginBottom: "16px" }}
				/>
			)}
			<Grid container spacing={2} sx={{ marginBottom: "8px" }}>
				<Grid item xs={4}>
					<TextField
						name="chat-name"
						label="Chat Name"
						fullWidth
						variant="outlined"
						value={chatName}
						onChange={(event) => setChatName(event.target.value)}
					/>
				</Grid>
				<Grid item xs={8}>
					<TextField
						name="description"
						label="Description"
						fullWidth
						multiline
						variant="outlined"
						value={chatDescription}
						onChange={(event) => setChatDescription(event.target.value)}
					/>
				</Grid>
			</Grid>
			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					paddingTop: "16px",
				}}
			>
				<Button variant="contained" color="primary" onClick={submitHandle}>
					Save chat
				</Button>
			</div>
		</Paper>
	);
}
